import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import { communicationService, Communication } from '../services/communicationService';
import { formatDateTime } from '../utils/dateUtils';
import { getApiError } from '../utils/apiError';
import RequirePermission from '../components/common/RequirePermission';
import TablePageLayout from '../components/table/TablePageLayout';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import CommunicationDetailModal from '../components/communications/CommunicationDetailModal';
import AssignCommunicationDialog from '../components/communications/AssignCommunicationDialog';

const preview = (c: Communication): string =>
  (c.body_text || '').replace(/\s+/g, ' ').trim();

const UnmatchedCommunicationsPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const userTimezone = useUserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [items, setItems] = useState<Communication[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Communication | null>(null);
  const [assignTarget, setAssignTarget] = useState<Communication | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await communicationService.getUnmatched({
        page: page + 1,
        page_size: rowsPerPage,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(getApiError(err, 'Failed to load unmatched communications'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const handleAssigned = () => {
    setAssignTarget(null);
    // If the last item on the page was assigned, step back a page.
    if (items.length === 1 && page > 0) {
      setPage((p) => p - 1);
    } else {
      load();
    }
  };

  const renderRows = () => (
    items.map((c) => (
      <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(c)}>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {formatDateTime(c.received_at || c.created_at, {}, userTimezone)}
        </TableCell>
        <TableCell sx={{ wordBreak: 'break-all' }}>{c.from_address || '-'}</TableCell>
        <TableCell sx={{ wordBreak: 'break-word' }}>
          {c.subject || '(no subject)'}
          {/* Says a file came with it; the row opens the message, which is
              where it can be downloaded. */}
          {!!c.attachments?.length && (
            <Box component="span" display="inline-flex" alignItems="center"
                 sx={{ ml: 1, color: 'text.secondary', verticalAlign: 'middle' }}
                 title={`${c.attachments.length} attachment(s)`}>
              <AttachFile sx={{ fontSize: 16 }} />
              <Typography variant="caption">{c.attachments.length}</Typography>
            </Box>
          )}
        </TableCell>
        <TableCell
          sx={{
            maxWidth: 320,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
          }}
        >
          {preview(c)}
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          <Button size="small" variant="contained" onClick={() => setAssignTarget(c)}>
            Assign
          </Button>
        </TableCell>
      </TableRow>
    ))
  );

  const renderMobile = () => (
    items.length === 0 ? (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        No unmatched communications
      </Typography>
    ) : (
      <Box display="flex" flexDirection="column" gap={2}>
        {items.map((c) => (
          <Card key={c.id} variant="outlined">
            <CardContent onClick={() => setSelected(c)} sx={{ cursor: 'pointer' }}>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(c.received_at || c.created_at, {}, userTimezone)}
              </Typography>
              <Typography variant="subtitle2" sx={{ wordBreak: 'break-all' }}>
                {c.from_address || '-'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                {c.subject || '(no subject)'}
                {!!c.attachments?.length && (
                  <Box component="span" display="inline-flex" alignItems="center"
                       sx={{ ml: 1, color: 'text.secondary', verticalAlign: 'middle' }}>
                    <AttachFile sx={{ fontSize: 16 }} />
                    <Typography variant="caption">{c.attachments.length}</Typography>
                  </Box>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {preview(c)}
              </Typography>
            </CardContent>
            <Box px={2} pb={2}>
              <Button size="small" variant="contained" fullWidth onClick={() => setAssignTarget(c)}>
                Assign to patient
              </Button>
            </Box>
          </Card>
        ))}
      </Box>
    )
  );

  return (
    <RequirePermission check={!!user?.user_privilege?.config} featureName="communications">
      <TablePageLayout loading={loading} error={error}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          Unmatched Communications
        </Typography>

        {isMobile ? (
          renderMobile()
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Received</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell>Preview</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No unmatched communications
                    </TableCell>
                  </TableRow>
                ) : (
                  renderRows()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />

        <CommunicationDetailModal
          comm={selected}
          onClose={() => setSelected(null)}
          userTimezone={userTimezone}
        />

        <AssignCommunicationDialog
          open={!!assignTarget}
          onClose={() => setAssignTarget(null)}
          comm={assignTarget}
          tenantId={currentTenant?.id ?? user?.tenant_id ?? 0}
          onAssigned={handleAssigned}
        />
      </TablePageLayout>
    </RequirePermission>
  );
};

export default UnmatchedCommunicationsPage;
